'use client';

import { useState } from 'react';
import { useNetworkSpeed, type NetQuality } from '@/lib/netSpeed';

/**
 * The connection, in the language bar, left of the Overview switch.
 *
 * It answers one question and it is the question people ask out loud when a
 * translator feels slow: "is it me?" Without it every delay looks the same from
 * the outside — a slow model, a busy server and a hotel wifi all show up as a
 * pane that has not filled in yet — and the only thing a visitor can do about
 * any of them is wonder. A number here separates the one cause they can act on
 * from the two they cannot.
 *
 * A CARD rather than a button, and that difference is deliberate: the two
 * controls beside it do something when pressed, and this does not. It is
 * readout, not switch, so it carries no border-on-hover, no lift, no pressed
 * state — nothing that would invite a click that leads nowhere.
 */

/**
 * Every quality band, in one place, because four things have to agree about it:
 * the bars, the ink, the word in the tooltip, and what a screen reader says.
 * Spread across four ternaries they would drift the first time a band moved.
 */
const BANDS: Record<NetQuality, { bars: number; label: string; note: string; tint: string }> = {
  fast:    { bars: 4, label: 'Fast',    note: 'Your connection is not what is slowing anything down.',
             tint: 'text-emerald-600 dark:text-emerald-400' },
  good:    { bars: 3, label: 'Good',    note: 'Fine for translating — nothing to fix here.',
             tint: 'text-emerald-600 dark:text-emerald-400' },
  slow:    { bars: 2, label: 'Slow',    note: 'Translations will arrive late even when the AI answers quickly.',
             tint: 'text-amber-600 dark:text-amber-400' },
  poor:    { bars: 1, label: 'Poor',    note: 'Long waits, and translations may not arrive at all.',
             tint: 'text-red-600 dark:text-red-400' },
  offline: { bars: 0, label: 'Offline', note: 'No connection — nothing can be translated until it returns.',
             tint: 'text-red-600 dark:text-red-400' },
  unknown: { bars: 0, label: 'Checking…', note: 'Measuring your connection to the translator.',
             tint: 'text-ink-400 dark:text-ink-500' },
};

/**
 * Mbps, at the precision the number deserves.
 *
 * `0.3` and `1.4` matter — they are the difference between a page that loads
 * and one that crawls — while `47.2` is noise dressed as measurement: nothing
 * on this page can tell 47 from 52. So the decimal is kept only where it
 * changes what somebody would do about it.
 */
function fmtMbps(mbps: number): string {
  return mbps >= 10 ? String(Math.round(mbps)) : mbps.toFixed(1);
}

/**
 * Four bars, filled to the band.
 *
 * The height differences carry the reading on their own, so the chip still says
 * "slow" in a black-and-white screenshot and to the ~8% of men who would read
 * the amber and the green as the same colour. Colour is the fast path here, not
 * the only one — the same rule the Overview bolt follows with its fill.
 */
function Bars({ filled }: { filled: number }) {
  return (
    <svg viewBox="0 0 16 12" className="h-3.5 w-[18px] shrink-0" aria-hidden>
      {[0, 1, 2, 3].map((i) => {
        const h = 3 + i * 3;
        return (
          <rect
            key={i}
            x={i * 4.2}
            y={12 - h}
            width="2.6"
            height={h}
            rx="1"
            fill="currentColor"
            // The unlit bars stay visible rather than disappearing: a single lit
            // bar with nothing beside it reads as a design element, while one lit
            // bar out of four reads as a measurement.
            opacity={i < filled ? 1 : 0.22}
          />
        );
      })}
    </svg>
  );
}

export default function NetworkChip() {
  const { rtt, mbps, quality, limiter, downlinkMbps, probing } = useNetworkSpeed();
  const [hovered, setHovered] = useState(false);
  const band = BANDS[quality];

  /*
   * The headline is the SPEED, because that is the word people use and the
   * number they can compare with what they are paying for. It falls back to the
   * delay for the second or two before the first block has been timed, and for
   * the visitor on Data Saver whose speed is deliberately never measured — in
   * both cases a real millisecond reading beats an empty chip.
   */
  const headline =
    mbps !== null ? { value: fmtMbps(mbps), unit: 'Mbps' }
    : rtt !== null ? { value: String(rtt), unit: 'ms' }
    : null;

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        role="status"
        // The bars and the number are decoration to a screen reader — this is
        // the whole reading, in words, and it updates politely rather than
        // interrupting whatever is being read.
        aria-label={
          [
            `Network: ${band.label}`,
            mbps !== null ? `${fmtMbps(mbps)} megabits per second` : null,
            rtt !== null ? `${rtt} millisecond${rtt === 1 ? '' : 's'} delay` : null,
          ]
            .filter(Boolean)
            .join(', ')
        }
        tabIndex={0}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        className={
          'flex h-9 cursor-default select-none items-center gap-1.5 rounded-lg border px-2.5 ' +
          'border-ink-200 bg-ink-50/60 dark:border-ink-700 dark:bg-ink-800/50 ' +
          band.tint
        }
      >
        <span className={probing && quality === 'unknown' ? 'animate-pulse' : undefined}>
          <Bars filled={band.bars} />
        </span>
        {/*
          The unit rides with the NUMBER and not with the chip, because there is
          no such thing as "off ms" — which is exactly what a permanently
          rendered unit produced the first time this ran offline.

          `tabular-nums` and a floor on the width so the language tabs never
          move. Without both, 98 ms becoming 1204 ms is two characters wider,
          and everything to its left shifts — on a row that is re-measured every
          25 seconds, which would make the whole bar twitch on a timer.
        */}
        {headline === null ? (
          <span className="text-xs font-medium">
            {quality === 'offline' ? 'Offline' : '—'}
          </span>
        ) : (
          <>
            <span className="min-w-[2.5ch] text-right text-xs font-medium tabular-nums">
              {headline.value}
            </span>
            <span className="text-[10px] text-ink-400 dark:text-ink-500">{headline.unit}</span>
          </>
        )}
      </div>

      {hovered && (
        <div
          role="tooltip"
          // Right-aligned, like the switch beside it, and for the reason that
          // rule exists: this whole group sits at the right edge of the bar, so
          // a card anchored to the chip's LEFT edge opens outwards and runs off
          // the side of the window — measured at 1400px, where it was clipped
          // mid-sentence. Anchoring it to the right edge opens it inwards over
          // the pane, where there is always room.
          className="pointer-events-none absolute right-0 top-full z-40 mt-1.5 w-64 rounded-lg
                     border border-ink-200 bg-white p-2.5 text-left shadow-xl
                     dark:border-ink-700 dark:bg-ink-800"
        >
          <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">
            Network · <span className={band.tint}>{band.label}</span>
          </p>
          {/* The two measurements, side by side and labelled, because they are
              different properties and the whole point of showing both is that a
              connection can be fine on one and bad on the other. */}
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5">
            <dt className="text-[11px] text-ink-400 dark:text-ink-500">Speed</dt>
            <dt className="text-[11px] text-ink-400 dark:text-ink-500">Delay</dt>
            <dd className="text-sm font-semibold tabular-nums text-ink-900 dark:text-ink-50">
              {mbps === null ? '—' : `${fmtMbps(mbps)} Mbps`}
            </dd>
            <dd className="text-sm font-semibold tabular-nums text-ink-900 dark:text-ink-50">
              {rtt === null ? '—' : `${rtt} ms`}
            </dd>
          </dl>

          <p className="mt-2 text-xs leading-relaxed text-ink-500 dark:text-ink-300">
            {band.note}
            {/* Naming the culprit only when the two halves disagree — see
                `limiter`. "Your connection is slow" is not actionable; "the
                delay is what is hurting you" tells somebody whether switching
                to wifi would help or whether they are simply far away. */}
            {limiter === 'speed' && ' Bandwidth is the tighter constraint here — the delay itself is fine.'}
            {limiter === 'delay' && ' The delay is the tighter constraint here, more than the bandwidth.'}
          </p>

          <p className="mt-1.5 text-[11px] leading-relaxed text-ink-400 dark:text-ink-500">
            Measured against this site: bytes timed on a real download, delay re-checked every 25
            seconds. It covers your connection only — not how long the AI takes to write the answer.
            {mbps === null && downlinkMbps !== null &&
              ` Your browser estimates about ${downlinkMbps} Mbps in the meantime.`}
          </p>
        </div>
      )}
    </div>
  );
}
